<template>
  <div class="indexed-by-google-container w-full max-w-7xl mx-auto px-4 py-6">
    <!-- Hidden File Input (always in DOM so $refs.fileInput is never null) -->
    <input
      ref="fileInput"
      type="file"
      accept=".log,.txt"
      class="hidden"
      @change="handleFileChange"
      :disabled="uploading"
    />

    <!-- ── Header Section ──────────────────────────────────────── -->
    <div
      class="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
    >
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div
            class="p-1.5 bg-blue-100 rounded-lg text-blue-600 flex items-center justify-center"
          >
            <Bot class="w-5 h-5" />
          </div>
          <h2 class="text-xl font-bold text-gray-800">
            Googlebot Log Inspector
          </h2>
          <span
            class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700"
          >
            Server Access Log Analysis
          </span>
        </div>
        <p class="text-xs text-gray-500">
          Upload server access logs to confirm Googlebot visits, track crawl
          frequency, and inspect which URLs were fetched.
        </p>
      </div>

      <div
        v-if="logData && logData.has_googlebot_data"
        class="flex items-center gap-2"
      >
        <button
          @click="exportCrawlDataToCSV"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition shadow-sm"
        >
          <Download class="w-3.5 h-3.5 text-gray-500" />
          <span>Export URLs (CSV)</span>
        </button>
        <button
          @click="triggerFileInput"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition shadow-sm"
        >
          <RefreshCw class="w-3.5 h-3.5 text-blue-600" />
          <span>Upload New Log</span>
        </button>
      </div>
    </div>

    <!-- ── File Upload Section (Dropzone) ───────────────────────── -->
    <div
      v-if="!logData || showUploadBox"
      class="relative mb-8 p-8 border-2 border-dashed rounded-xl transition-all duration-200 bg-white shadow-sm text-center"
      :class="{
        'border-blue-400 bg-blue-50/50 ring-4 ring-blue-100': isDragging,
        'border-gray-300 hover:border-gray-400': !isDragging,
      }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
    >
      <!-- Close button when user wants to dismiss upload box -->
      <button
        v-if="logData && showUploadBox"
        type="button"
        @click="showUploadBox = false"
        class="absolute top-3 right-3 text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100 transition"
        title="Close Upload Box"
      >
        <X class="w-4 h-4" />
      </button>

      <div v-if="uploading" class="py-6 flex flex-col items-center">
        <div
          class="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"
        ></div>
        <h4 class="text-base font-semibold text-gray-800 mb-1">
          Processing Access Log...
        </h4>
        <p class="text-xs text-gray-500 max-w-md">
          Streaming and parsing log entries with multi-format pattern matching.
          Large files (up to 500 MB) are processed line-by-line safely.
        </p>
      </div>

      <div v-else class="flex flex-col items-center">
        <div
          class="w-16 h-16 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-3 shadow-inner"
        >
          <FolderOpen class="w-8 h-8 text-blue-600" />
        </div>
        <h3 class="text-base font-bold text-gray-800 mb-1">
          Upload Server Access Log
        </h3>
        <p class="text-xs text-gray-500 max-w-lg mb-4">
          Drag and drop your server log file here, or click to browse. Supports
          files up to 500 MB.
        </p>

        <button
          type="button"
          @click="triggerFileInput"
          class="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-sm hover:shadow transition-all inline-flex items-center gap-2"
        >
          <Paperclip class="w-3.5 h-3.5" />
          <span>Select File (.log or .txt)</span>
        </button>

        <div
          class="mt-5 flex flex-wrap justify-center items-center gap-2 text-[11px] text-gray-500"
        >
          <span class="font-medium text-gray-600">Supported Formats:</span>
          <span class="px-2 py-0.5 bg-gray-100 rounded text-gray-600"
            >Nginx Combined</span
          >
          <span class="px-2 py-0.5 bg-gray-100 rounded text-gray-600"
            >Apache Combined / Common</span
          >
          <span class="px-2 py-0.5 bg-gray-100 rounded text-gray-600"
            >Cloudflare Proxy</span
          >
          <span class="px-2 py-0.5 bg-gray-100 rounded text-gray-600"
            >IIS (W3C Format)</span
          >
          <span class="px-2 py-0.5 bg-gray-100 rounded text-gray-600"
            >AWS ALB</span
          >
        </div>

        <p
          v-if="selectedFileName"
          class="mt-3 text-xs text-gray-600 font-medium"
        >
          Selected: <span class="text-blue-600">{{ selectedFileName }}</span>
        </p>

        <div
          v-if="errorMessage"
          class="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex items-center gap-2"
        >
          <AlertCircle class="w-4 h-4 text-red-500 shrink-0" />
          <span>{{ errorMessage }}</span>
        </div>
      </div>
    </div>

    <!-- ── Upload Success Banner if logData is present ──────────── -->
    <div
      v-if="successMessage && logData"
      class="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-lg flex items-center justify-between"
    >
      <div class="flex items-center gap-2">
        <CheckCircle2 class="w-4 h-4 text-emerald-600 shrink-0" />
        <span>{{ successMessage }}</span>
      </div>
      <button
        @click="successMessage = ''"
        class="text-emerald-600 hover:text-emerald-800 p-1 rounded hover:bg-emerald-100/50 transition ml-4"
      >
        <X class="w-3.5 h-3.5" />
      </button>
    </div>

    <!-- ── Dashboard Content (when logData is present) ─────────── -->
    <div v-if="logData">
      <!-- 1. Activity Status Alert when 0 visits -->
      <div
        v-if="!logData.has_googlebot_data"
        class="mb-6 p-6 bg-amber-50 border border-amber-200 rounded-xl text-center"
      >
        <div
          class="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto mb-3"
        >
          <Search class="w-6 h-6" />
        </div>
        <h4 class="text-base font-bold text-amber-800 mb-1">
          No Googlebot Activity Detected
        </h4>
        <p class="text-xs text-amber-700 max-w-lg mx-auto mb-4">
          The uploaded log file does not contain any requests with a "Googlebot"
          User-Agent header. Your site might be new, blocked by robots.txt, or
          the uploaded log window does not cover a crawl period.
        </p>
        <button
          @click="triggerFileInput"
          class="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-md transition shadow-sm inline-flex items-center gap-1.5"
        >
          <RefreshCw class="w-3.5 h-3.5" />
          <span>Upload Another Log File</span>
        </button>
      </div>

      <!-- 2. Summary Metric Cards -->
      <div v-else class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <!-- Card 1: Crawl Status -->
          <div class="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <span
                class="text-xs font-semibold uppercase tracking-wider text-gray-500"
                >Googlebot Status</span
              >
              <div class="p-1 bg-emerald-50 rounded text-emerald-600">
                <ShieldCheck class="w-4 h-4" />
              </div>
            </div>
            <div class="flex items-center gap-1.5 mb-1">
              <span
                class="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"
              ></span>
              <span class="text-lg font-bold text-gray-900"
                >Confirmed Active</span
              >
            </div>
            <p class="text-[11px] text-emerald-600 font-medium">
              Googlebot successfully crawled your site.
            </p>
          </div>

          <!-- Card 2: Total Visits -->
          <div class="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <span
                class="text-xs font-semibold uppercase tracking-wider text-gray-500"
                >Total Crawl Hits</span
              >
              <div class="p-1 bg-blue-50 rounded text-blue-600">
                <BarChart2 class="w-4 h-4" />
              </div>
            </div>
            <div class="text-2xl font-extrabold text-gray-900 mb-2">
              {{ formatNumber(logData.summary?.total_visits || 0) }}
            </div>
            <div class="flex items-center gap-2">
              <span
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-50 text-blue-700"
              >
                <Monitor class="w-3 h-3" />
                {{ logData.summary?.desktop_visits || 0 }} desktop
              </span>
              <span
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-purple-50 text-purple-700"
              >
                <Smartphone class="w-3 h-3" />
                {{ logData.summary?.mobile_visits || 0 }} mobile
              </span>
            </div>
          </div>

          <!-- Card 3: Unique Pages -->
          <div class="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <span
                class="text-xs font-semibold uppercase tracking-wider text-gray-500"
                >Unique URLs Crawled</span
              >
              <div class="p-1 bg-indigo-50 rounded text-indigo-600">
                <Link2 class="w-4 h-4" />
              </div>
            </div>
            <div class="text-2xl font-extrabold text-gray-900 mb-1">
              {{ formatNumber(logData.summary?.unique_pages || 0) }}
            </div>
            <p class="text-[11px] text-gray-500">
              Distinct URLs requested with 2xx HTTP status.
            </p>
          </div>

          <!-- Card 4: Date Range -->
          <div class="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <span
                class="text-xs font-semibold uppercase tracking-wider text-gray-500"
                >Crawl Window</span
              >
              <div class="p-1 bg-purple-50 rounded text-purple-600">
                <Calendar class="w-4 h-4" />
              </div>
            </div>
            <div class="text-xs text-gray-700 space-y-1">
              <p>
                <span class="text-gray-400">Last Crawled:</span>
                <span class="font-bold text-gray-900 block truncate">{{
                  logData.summary?.last_crawl || "—"
                }}</span>
              </p>
              <p>
                <span class="text-gray-400">First Crawled:</span>
                <span class="font-medium text-gray-600 block truncate">{{
                  logData.summary?.first_crawl || "—"
                }}</span>
              </p>
            </div>
          </div>
        </div>

        <!-- 3. Daily Crawl Activity Chart / Timeline -->
        <div
          v-if="dailyCountsList.length > 0"
          class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm"
        >
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-1.5">
              <TrendingUp class="w-4 h-4 text-blue-600" />
              <div>
                <h4 class="text-sm font-bold text-gray-800">
                  Daily Crawl Frequency
                </h4>
                <p class="text-xs text-gray-500">
                  Number of Googlebot visits recorded per day in the access log.
                </p>
              </div>
            </div>
            <span class="text-xs text-gray-400 font-medium">
              {{ dailyCountsList.length }} days recorded
            </span>
          </div>

          <div
            class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 lg:grid-cols-10 gap-2 pt-2"
          >
            <div
              v-for="item in dailyCountsList"
              :key="item.date"
              class="p-2.5 rounded-lg border border-gray-100 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 transition text-center"
            >
              <span class="block text-[10px] text-gray-500 font-mono mb-1">
                {{ item.date }}
              </span>
              <span class="block text-sm font-bold text-blue-700">
                {{ item.count }}
              </span>
              <span class="block text-[9px] text-gray-400"> visits </span>
            </div>
          </div>
        </div>

        <!-- 4. Crawled URLs Explorer -->
        <div
          class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
        >
          <div
            class="p-5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
          >
            <div class="flex items-center gap-1.5">
              <Globe class="w-4 h-4 text-blue-600" />
              <div>
                <h4 class="text-sm font-bold text-gray-800">
                  Crawled URLs ({{ filteredPages.length }})
                </h4>
                <p class="text-xs text-gray-500">
                  Endpoints fetched by Googlebot that returned successful HTTP
                  response codes.
                </p>
              </div>
            </div>

            <!-- Search input -->
            <div class="relative w-full sm:w-64">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search path (e.g. /about, /api)..."
                class="w-full text-xs border border-gray-300 rounded-lg pl-8 pr-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search
                class="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2"
              />
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full text-left text-xs text-gray-600">
              <thead
                class="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200"
              >
                <tr>
                  <th class="py-2.5 px-4 w-12 text-center">#</th>
                  <th class="py-2.5 px-4">URL Path</th>
                  <th class="py-2.5 px-4 w-28 text-center">Status</th>
                  <th class="py-2.5 px-4 w-24 text-right">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr
                  v-for="(page, idx) in paginatedPages"
                  :key="page"
                  class="hover:bg-gray-50/80 transition"
                >
                  <td
                    class="py-2.5 px-4 text-center font-mono text-gray-400 text-[11px]"
                  >
                    {{ (currentPage - 1) * pageSize + idx + 1 }}
                  </td>
                  <td
                    class="py-2.5 px-4 font-mono font-medium text-gray-800 break-all"
                  >
                    {{ page }}
                  </td>
                  <td class="py-2.5 px-4 text-center">
                    <span
                      class="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800"
                    >
                      200 OK
                    </span>
                  </td>
                  <td class="py-2.5 px-4 text-right">
                    <button
                      @click="copyToClipboard(page)"
                      class="inline-flex items-center gap-1 text-gray-400 hover:text-blue-600 transition text-[11px]"
                      title="Copy URL"
                    >
                      <Copy class="w-3 h-3" />
                      <span>Copy</span>
                    </button>
                  </td>
                </tr>
                <tr v-if="paginatedPages.length === 0">
                  <td
                    colspan="4"
                    class="py-8 text-center text-gray-400 text-xs"
                  >
                    No matching URLs found for "{{ searchQuery }}".
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div
            v-if="totalPages > 1"
            class="p-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-600"
          >
            <div>
              Showing {{ (currentPage - 1) * pageSize + 1 }} to
              {{ Math.min(currentPage * pageSize, filteredPages.length) }} of
              {{ filteredPages.length }} pages
            </div>
            <div class="flex items-center gap-1">
              <button
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="px-2.5 py-1 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 inline-flex items-center gap-1"
              >
                <ChevronLeft class="w-3.5 h-3.5" />
                <span>Prev</span>
              </button>
              <span class="px-2 font-medium">
                Page {{ currentPage }} of {{ totalPages }}
              </span>
              <button
                @click="currentPage++"
                :disabled="currentPage >= totalPages"
                class="px-2.5 py-1 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 inline-flex items-center gap-1"
              >
                <span>Next</span>
                <ChevronRight class="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        <!-- 5. Recent Visits Activity Stream -->
        <div
          v-if="logData.recent_visits && logData.recent_visits.length > 0"
          class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
        >
          <div class="p-5 border-b border-gray-200 flex items-center gap-1.5">
            <Clock class="w-4 h-4 text-gray-600" />
            <div>
              <h4 class="text-sm font-bold text-gray-800">
                Recent Googlebot Visits
              </h4>
              <p class="text-xs text-gray-500">
                Latest individual crawl requests captured in the log.
              </p>
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full text-left text-xs text-gray-600">
              <thead
                class="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200"
              >
                <tr>
                  <th class="py-2.5 px-4 w-44">Timestamp</th>
                  <th class="py-2.5 px-4 w-28">Crawler Type</th>
                  <th class="py-2.5 px-4">Requested URL</th>
                  <th class="py-2.5 px-4 w-24 text-center">HTTP Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 font-mono text-[11px]">
                <tr
                  v-for="(visit, idx) in logData.recent_visits"
                  :key="idx"
                  class="hover:bg-gray-50/80 transition"
                >
                  <td class="py-2.5 px-4 text-gray-600">
                    {{ visit.date }}
                  </td>
                  <td class="py-2.5 px-4">
                    <span
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold"
                      :class="
                        visit.agent_type === 'mobile'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      "
                    >
                      <Smartphone
                        v-if="visit.agent_type === 'mobile'"
                        class="w-3 h-3"
                      />
                      <Monitor v-else class="w-3 h-3" />
                      {{ visit.agent_type === "mobile" ? "Mobile" : "Desktop" }}
                    </span>
                  </td>
                  <td class="py-2.5 px-4 text-gray-800 break-all">
                    {{ visit.url }}
                  </td>
                  <td class="py-2.5 px-4 text-center">
                    <span
                      class="px-2 py-0.5 rounded text-[10px] font-semibold"
                      :class="{
                        'bg-emerald-100 text-emerald-800':
                          visit.status >= 200 && visit.status < 300,
                        'bg-amber-100 text-amber-800':
                          visit.status >= 300 && visit.status < 400,
                        'bg-red-100 text-red-800': visit.status >= 400,
                      }"
                    >
                      {{ visit.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 6. Educational SEO Guidance Card -->
        <div
          class="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/80 rounded-xl text-xs text-blue-900 flex items-start gap-3"
        >
          <div class="p-1.5 bg-blue-100 rounded-lg text-blue-700 shrink-0">
            <Lightbulb class="w-5 h-5" />
          </div>
          <div>
            <h5 class="font-bold text-sm text-blue-900 mb-1">
              SEO Best Practice: Crawling vs. Indexing
            </h5>
            <p class="leading-relaxed text-blue-800/90">
              A recorded Googlebot visit proves search engine discovery, but
              indexing requires your pages to meet quality standards. To ensure
              crawled pages are indexed in Google Search results, verify they
              return a clean
              <code
                class="px-1 py-0.5 bg-blue-100/80 rounded text-blue-900 font-mono text-[11px]"
                >200 OK</code
              >
              status, define canonical URLs, and avoid unintentional
              <code
                class="px-1 py-0.5 bg-blue-100/80 rounded text-blue-900 font-mono text-[11px]"
                >noindex</code
              >
              tags or robots.txt restrictions. You can audit these on-page
              directives in the <strong>Diagnostic Results</strong> tab.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Empty State (before any file is uploaded) ───────────── -->
    <div
      v-else-if="!uploading"
      class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4"
    >
      <div
        class="p-5 bg-white border border-gray-200 rounded-xl shadow-sm text-center"
      >
        <div
          class="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-3"
        >
          <ScanSearch class="w-6 h-6" />
        </div>
        <h4 class="text-sm font-bold text-gray-800 mb-1">
          Verify Bot Discovery
        </h4>
        <p class="text-xs text-gray-500 leading-relaxed">
          Confirm that Googlebot has discovered and visited your domain by
          inspecting real server request timestamps.
        </p>
      </div>

      <div
        class="p-5 bg-white border border-gray-200 rounded-xl shadow-sm text-center"
      >
        <div
          class="w-12 h-12 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center mx-auto mb-3"
        >
          <Smartphone class="w-6 h-6" />
        </div>
        <h4 class="text-sm font-bold text-gray-800 mb-1">
          Mobile-First Inspection
        </h4>
        <p class="text-xs text-gray-500 leading-relaxed">
          Compare crawl volume between Googlebot Mobile (Smartphone) and Desktop
          crawlers for indexing health.
        </p>
      </div>

      <div
        class="p-5 bg-white border border-gray-200 rounded-xl shadow-sm text-center"
      >
        <div
          class="w-12 h-12 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-3"
        >
          <Zap class="w-6 h-6" />
        </div>
        <h4 class="text-sm font-bold text-gray-800 mb-1">
          Stream-Based Performance
        </h4>
        <p class="text-xs text-gray-500 leading-relaxed">
          Log files up to 500 MB are streamed securely line-by-line in
          milliseconds without overloading your browser or server memory.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Bot,
  Download,
  RefreshCw,
  FolderOpen,
  Paperclip,
  AlertCircle,
  CheckCircle2,
  Search,
  ShieldCheck,
  BarChart2,
  Monitor,
  Smartphone,
  Link2,
  Calendar,
  TrendingUp,
  Globe,
  Copy,
  Clock,
  Lightbulb,
  ScanSearch,
  Zap,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-vue-next";

export default {
  name: "IndexedByGoogle",
  components: {
    Bot,
    Download,
    RefreshCw,
    FolderOpen,
    Paperclip,
    AlertCircle,
    CheckCircle2,
    Search,
    ShieldCheck,
    BarChart2,
    Monitor,
    Smartphone,
    Link2,
    Calendar,
    TrendingUp,
    Globe,
    Copy,
    Clock,
    Lightbulb,
    ScanSearch,
    Zap,
    X,
    ChevronLeft,
    ChevronRight,
  },
  props: {
    initialLogData: {
      type: Object,
      default: null,
    },
    uploadingProp: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      logData: this.initialLogData || null,
      uploading: this.uploadingProp,
      isDragging: false,
      selectedFileName: "",
      errorMessage: "",
      successMessage: "",
      showUploadBox: false,
      searchQuery: "",
      currentPage: 1,
      pageSize: 15,
    };
  },
  watch: {
    initialLogData(newVal) {
      if (newVal) {
        this.logData = newVal;
        this.uploading = false;
        if (newVal.has_googlebot_data) {
          this.successMessage = `Found ${
            newVal.summary?.total_visits || 0
          } Googlebot visits across ${
            newVal.summary?.unique_pages || 0
          } unique URLs.`;
        }
      }
    },
    uploadingProp(newVal) {
      this.uploading = newVal;
    },
    searchQuery() {
      this.currentPage = 1;
    },
  },
  computed: {
    dailyCountsList() {
      if (!this.logData?.daily_counts) return [];
      return Object.entries(this.logData.daily_counts).map(([date, count]) => ({
        date,
        count,
      }));
    },
    filteredPages() {
      if (!this.logData?.pages_crawled) return [];
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.logData.pages_crawled;
      return this.logData.pages_crawled.filter((path) =>
        path.toLowerCase().includes(query)
      );
    },
    paginatedPages() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredPages.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.ceil(this.filteredPages.length / this.pageSize) || 1;
    },
  },
  methods: {
    triggerFileInput() {
      this.errorMessage = "";
      this.$nextTick(() => {
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = "";
          this.$refs.fileInput.click();
        }
      });
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    processFile(file) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["log", "txt"].includes(ext)) {
        this.errorMessage =
          "Please upload a valid .log or .txt server access log file.";
        return;
      }

      const maxBytes = 500 * 1024 * 1024; // 500 MB
      if (file.size > maxBytes) {
        this.errorMessage =
          "File exceeds the 500 MB limit. Please select a smaller log file.";
        return;
      }

      this.selectedFileName = file.name;
      this.errorMessage = "";
      this.uploading = true;
      this.showUploadBox = false;

      // Emit file upload to parent (DashboardView.vue)
      this.$emit("upload-access-log", file);
    },
    formatNumber(num) {
      return Number(num).toLocaleString();
    },
    copyToClipboard(text) {
      navigator.clipboard?.writeText(text).then(() => {
        alert("Copied URL to clipboard: " + text);
      });
    },
    exportCrawlDataToCSV() {
      if (!this.logData?.pages_crawled?.length) return;

      const rows = [["Crawled URL", "HTTP Status"]];
      this.logData.pages_crawled.forEach((url) => {
        rows.push([url, "200"]);
      });

      const csvContent =
        "data:text/csv;charset=utf-8," +
        rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute(
        "download",
        `googlebot_crawled_urls_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.indexed-by-google-container {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
